import { ISpeaker } from './interfaces';

/**
 * SPEAKER IDENTITY SYSTEM
 * 
 * The transcript system uses two distinct identifier types:
 * 
 * 1. Speaker Labels (Anonymous IDs):
 *    - Format: "spk_X" (e.g., "spk_0", "spk_1")
 *    - Usage: Internal to transcript segments
 *    - Storage: Used as the "speakerLabel" field in this TranscriptSpeaker class
 *    - Purpose: Track speakers within the transcript structure
 * 
 * 2. Speaker IDs (Dato CMS IDs):
 *    - Format: Various, but NEVER starts with "spk_"
 *    - Usage: External reference to named speakers in CMS
 *    - Storage: Used as the "datoSpeakerId" field in this TranscriptSpeaker class
 *    - Purpose: Link to persistent speakers in backend
 * 
 * The mapping between these two ID types is maintained in TranscriptVoiceMapping.
 * When displaying speakers, names should always come from the Dato speaker (via datoSpeakerId),
 * not directly from the anonymous speaker label.
 */

/**
 * Represents a speaker in a transcript
 */
export class TranscriptSpeaker implements ISpeaker {
  id: string;
  speakerLabel: string;
  datoSpeakerId?: string;
  name?: string;
  title?: string;
  voice?: string;
  bio?: string;
  color?: string;

  /**
   * Creates a new transcript speaker
   * 
   * @param speakerLabel Unique speaker label in "spk_X" format
   * @param datoSpeakerId Optional Dato CMS identifier for the speaker
   * @param name Optional display name for the speaker
   * @param color Optional color code for UI display
   */
  constructor(
    speakerLabel: string,
    datoSpeakerId?: string,
    name?: string,
    color?: string,
    title?: string,
    voice?: string,
    bio?: string
  ) {
    this.id = speakerLabel; // Use speakerLabel as the entity ID
    this.speakerLabel = speakerLabel;
    this.datoSpeakerId = datoSpeakerId;
    this.name = name;
    this.color = color;
    this.title = title;
    this.voice = voice;
    this.bio = bio;
  }

  /**
   * Creates a TranscriptSpeaker instance from a plain object
   *
   * @param data Plain object with speaker data
   * @returns A new TranscriptSpeaker instance
   */
  static fromObject(data: any): TranscriptSpeaker {
    // Support both old and new field names for backward compatibility
    const speakerLabel = data.speakerLabel || data.id;
    const datoSpeakerId = data.datoSpeakerId || data.datoId;

    return new TranscriptSpeaker(
      speakerLabel,
      datoSpeakerId,
      data.name,
      data.color,
      data.title,
      data.voice,
      data.bio
    );
  }

  /**
   * Converts the speaker to a plain object for serialization
   *
   * @returns Plain object representation of the speaker
   */
  toJSON(): Record<string, any> {
    return {
      id: this.id,
      speakerLabel: this.speakerLabel,
      datoSpeakerId: this.datoSpeakerId,
      name: this.name,
      title: this.title,
      bio: this.bio,
      voice: this.voice,
      color: this.color
    };
  }

  /**
   * Get display name for the speaker
   * If name is not set, returns a formatted ID
   * 
   * @returns Display name
   */
  getDisplayName(): string {
    if (this.name) return this.name;
    
    // If no name is set, use "Speaker X" format
    return `Speaker ${this.speakerLabel.replace('spk_', '')}`;
  }
}